import { DateTime } from 'luxon';

import { Segment } from './availability.model';

export const DAY_MINUTES = 24 * 60;
export const MIN_SEG_MINUTES = 30;
export const SNAP_MINUTES = 15;
export const MAX_TOTAL_MINUTES = 16 * 60;
export const MAX_SEGMENTS_PER_DAY = 4;

export function snap(min: number): number {
  return Math.round(min / SNAP_MINUTES) * SNAP_MINUTES;
}

export function floorToStep(min: number, step = SNAP_MINUTES): number {
  return Math.floor(min / step) * step;
}

export function clamp(value: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, value));
}

export function rangeOverlaps(a: Segment, b: Segment): boolean {
  return a.startTime < b.endTime && b.startTime < a.endTime;
}

export function nextSegmentId(): string {
  return self.crypto.randomUUID();
}

export function totalMinutes(segments: Segment[]): number {
  return segments.reduce((acc, s) => acc + (s.endTime - s.startTime), 0);
}

export function isSegmentListValid(segments: Segment[]): boolean {
  const sorted = [...segments].sort((a, b) => a.startTime - b.startTime);
  for (const s of sorted) {
    if (s.startTime < 0 || s.endTime > DAY_MINUTES) {
      return false;
    }
    if (s.endTime - s.startTime < MIN_SEG_MINUTES) {
      return false;
    }
  }
  for (let i = 1; i < sorted.length; i += 1) {
    if (rangeOverlaps(sorted[i - 1], sorted[i])) {
      return false;
    }
  }
  if (totalMinutes(sorted) > MAX_TOTAL_MINUTES) {
    return false;
  }
  return true;
}

export function formatTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function formatDateLabel(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) {
    return isoDate;
  }
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}

export function nextNDays(n: number, fromIsoDate?: string): string[] {
  const start = fromIsoDate
    ? DateTime.fromISO(fromIsoDate, { zone: 'local' }).startOf('day')
    : DateTime.local().startOf('day').plus({ days: 1 });
  return Array.from({ length: n }, (_, index) =>
    start.plus({ days: index }).toFormat('yyyy-MM-dd')
  );
}
